"use client";

import type { Tickets as AutoTickets, Ticket as TicketType } from "@types";
import Image from "next/image";
import { ScrollParallax } from "react-just-parallax";
import Button from "@/components/inputs/Button";
import { spacing, typography } from "@/styles/design-tokens";
import { BlockContent } from "./inputs/PortableTextComponents";

type ExpandedTickets = Omit<AutoTickets, "tickets"> & {
  tickets?: TicketType[];
};

export default function Tickets({ title, copy, tickets }: ExpandedTickets) {
  if (!tickets) return null;

  function Ticket({ tag, price, name, info, button }: TicketType) {
    if (!button) return null;

    return (
      <div className="relative border-2 rounded-2xl bg-yellow p-2 shadow-[8px_8px_0_0_rgba(0,0,0,0.25)] hover:-translate-y-4 transition-all">
        <div className="bg-white rounded-lg w-full h-full flex flex-col pt-10 pb-6 px-4 gap-6 lg:px-6">
          {tag && (
            <p
              className={`${typography.caption} absolute -top-0 right-10 bg-yellow border-2 rounded-sm py-1.5 px-3 -translate-y-1/2`}
            >
              {tag}
            </p>
          )}

          <div className="w-full flex flex-wrap gap-2 pb-6 border-b-2 justify-between">
            {name && (
              <p className="font-black uppercase font-display text-3xl tracking-wide">
                {name}
              </p>
            )}
            {price && <p className={typography.captionLarge}>{price}</p>}
          </div>

          {info && <BlockContent value={info} classes="flex-1 h-full" />}
          <div className="pt-6 border-t-2">
            <Button {...button} classes="!w-full" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <section className={spacing.section}>
      <div className={spacing.container}>
        <div className="flex items-center space-between">
          <div className="hidden sm:block">
            <ScrollParallax>
              <Image
                src="/images/sushi-roll.gif"
                alt=""
                width={96}
                height={96}
              />
            </ScrollParallax>
          </div>
          <div className="text-center space-y-4 max-w-4xl mx-auto">
            {title && <h2 className={`${typography.h5} sm:px-8`}>{title}</h2>}
            {copy && <BlockContent value={copy} />}
          </div>
          <div className="hidden sm:block pr:10 pb-5">
            <ScrollParallax strength={0.32}>
              <Image
                src="/images/headphones.gif"
                alt=""
                width={96}
                height={96}
              />
            </ScrollParallax>
          </div>
        </div>
        <div className="grid gap-10 xl:grid-cols-3 xl:gap-6 xl:pt-4">
          {tickets.map((ticket) => (
            <Ticket key={ticket._id} {...ticket} />
          ))}
        </div>
      </div>
    </section>
  );
}
